import { useMemo, useSyncExternalStore } from 'react';
import { generatedVersion, subscribeGenerated } from '../domain/generated';
import { RECIPES, type MealType } from '../domain/recipes';
import { toUseFirst } from '../domain/freshness';
import { rankRecipes, type RankedRecipe } from '../domain/ranking';
import { recommend, type Recommendation } from '../domain/recommend';
import { learn, type Learned } from '../domain/learn';
import { computeProgress, type Progress } from '../domain/progress';
import type { ListContext } from '../domain/grocery';
import { todayISO } from '../lib/dates';
import { useStore } from './store';
import { effectiveGoals } from '../domain/plans';

/** Recipes ranked against the CONFIRMED kitchen and the user's hard rules (all meal types). */
export function useRanked(): RankedRecipe[] {
  const kitchen = useStore((s) => s.kitchen.items);
  const profile = useStore((s) => s.profile);
  const cooked = useStore((s) => s.cooked);
  return useMemo(
    () =>
      rankRecipes(RECIPES.filter((r) => !profile.hiddenRecipes.includes(r.id)), {
        kitchen: kitchen.map((i) => i.ingredientId),
        useSoon: toUseFirst(kitchen),
        staples: profile.staples,
        rules: profile.rules,
        dislikes: profile.dislikes,
        styles: profile.styles,
        recentlyCooked: cooked.slice(0, 14).map((c) => c.recipeId),
        stock: kitchen,
        servings: profile.household,
      }),
    [kitchen, profile, cooked],
  );
}

export function useLearned(): Learned {
  const cooked = useStore((s) => s.cooked);
  const feedback = useStore((s) => s.feedback);
  const inferenceOverrides = useStore((s) => s.inferenceOverrides);
  const learning = useStore((s) => s.profile.learning);
  return useMemo(() => learn({ cooked, feedback, inferenceOverrides, learning }), [cooked, feedback, inferenceOverrides, learning]);
}

/** Personalised, explained recommendations for one or more meal types. */
export function useRecommendations(meals: MealType[] = ['main'], occasion?: { ignoreKitchen: boolean }): Recommendation[] {
  const kitchen = useStore((s) => s.kitchen.items);
  const profile = useStore((s) => s.profile);
  const cooked = useStore((s) => s.cooked);
  const feedback = useStore((s) => s.feedback);
  const learned = useLearned();
  const membership = useStore((s) => s.membership);
  // Recipes Amari writes join the pool as they arrive.
  const written = useGeneratedVersion();
  const key = meals.join(',');
  return useMemo(() => {
    const dayAgo = Date.now() - 86_400_000;
    // Fitness goals only shape picks with Amari Plus.
    const applied = { ...profile, goals: effectiveGoals(profile.goals, membership) };
    return recommend({
      // Occasions allow a longer cook than the weeknight limit.
      profile: occasion ? { ...applied, maxMinutes: Math.max(applied.maxMinutes, 90) } : applied,
      ignoreKitchen: occasion?.ignoreKitchen,
      kitchen: kitchen.map((i) => i.ingredientId),
      useSoon: toUseFirst(kitchen),
      recentlyCooked: cooked.slice(0, 14).map((c) => c.recipeId),
      recentlyDeclined: feedback.filter((f) => f.type === 'not_today' && new Date(f.at).getTime() > dayAgo).map((f) => (f as { recipeId: string }).recipeId),
      learned,
      meals: key.split(',') as MealType[],
      stock: kitchen,
    });
  }, [kitchen, profile, cooked, feedback, learned, key, occasion?.ignoreKitchen, membership, written]); // eslint-disable-line react-hooks/exhaustive-deps
}

export function useProgress(): Progress {
  const cooked = useStore((s) => s.cooked);
  const feedback = useStore((s) => s.feedback);
  const plan = useStore((s) => s.plan);
  const planHistory = useStore((s) => s.planHistory);
  const weights = useStore((s) => s.weights);
  return useMemo(() => computeProgress({ cooked, feedback, plan, planHistory, weights, today: todayISO() }), [cooked, feedback, plan, planHistory, weights]);
}

export function useListContext(): ListContext {
  const kitchen = useStore((s) => s.kitchen.items);
  const profile = useStore((s) => s.profile);
  return useMemo(
    () => ({ kitchen: kitchen.map((i) => i.ingredientId), staples: profile.staples, rules: profile.rules, dislikes: profile.dislikes, stock: kitchen }),
    [kitchen, profile],
  );
}

/** Re-renders when a recipe Amari wrote is added or gets its photo. */
export function useGeneratedVersion(): number {
  return useSyncExternalStore(subscribeGenerated, generatedVersion, generatedVersion);
}
